import classNames from 'classnames';
import React, {useEffect} from 'react';

import {sendAnalytics} from '@cdo/apps/aichat/redux';
import {jsonVideoRehypeMap} from '@cdo/apps/jsonVideo/jsonVideoRehypeMap';
import {EVENTS} from '@cdo/apps/metrics/AnalyticsConstants';
import {getStore} from '@cdo/apps/redux';
import SafeMarkdown from '@cdo/apps/templates/SafeMarkdown';
import {commonI18n} from '@cdo/apps/types/locale';
import {useAppSelector} from '@cdo/apps/util/reduxHooks';
import aiBotOutlineIcon from '@cdo/static/ai-bot-outline.png';

import AiTutorVersionActions from '../aiTutorVersionActions/AiTutorVersionActions';
import CopyableCodeBlock from '../copyableCodeBlock/CopyableCodeBlock';

import {Role} from './types';

import moduleStyles from './chat-message.module.scss';
interface ChatMessageProps {
  text: string;
  postText?: React.ReactNode;
  role: Role;
  customStyles?: {[label: string]: string};
  header?: React.ReactNode;
  footer?: React.ReactNode;
  isTA?: boolean;
  messageStyle?: 'default' | 'warning' | 'danger';
  isAiTutorVersion?: boolean;
  isLastMessage?: boolean;
}

const codeCopiedAnalytics = (isTA: boolean) => () =>
  getStore().dispatch(sendAnalytics(EVENTS.CODE_COPIED, {isTA: isTA}));

const taRehypeMap = {
  pre: (props: React.ComponentPropsWithoutRef<'pre'>) => (
    <CopyableCodeBlock {...props} onCopy={codeCopiedAnalytics(true)} />
  ),
  ...jsonVideoRehypeMap,
};

const nonTaRehypeMap = {
  pre: (props: React.ComponentPropsWithoutRef<'pre'>) => (
    <CopyableCodeBlock {...props} onCopy={codeCopiedAnalytics(false)} />
  ),
  ...jsonVideoRehypeMap,
};

const ChatMessage: React.FunctionComponent<ChatMessageProps> = ({
  text,
  postText,
  role,
  customStyles,
  header,
  footer,
  isTA,
  messageStyle = 'default',
  isAiTutorVersion = false,
  isLastMessage = false,
}) => {
  const rehypeMap = isTA ? taRehypeMap : nonTaRehypeMap;

  const aiTutorVersionFiles = useAppSelector(
    state => state.weblab2?.aiTutorVersionFiles || []
  );

  const showAiTutorVersionActions =
    isAiTutorVersion && isLastMessage && aiTutorVersionFiles.length > 0;

  // Show browser warning when user attempts to reload the page before accepting or rejecting AI Tutor's proposed updates.
  useEffect(() => {
    if (showAiTutorVersionActions) {
      const handleBeforeUnload = (event: BeforeUnloadEvent) => {
        event.preventDefault();
        // Chrome requires returnValue to be set.
        event.returnValue = '';
      };

      window.addEventListener('beforeunload', handleBeforeUnload);

      return () => {
        window.removeEventListener('beforeunload', handleBeforeUnload);
      };
    }
  }, [showAiTutorVersionActions]);

  const isAssistant = role === Role.ASSISTANT;

  return (
    <div
      className={classNames(
        moduleStyles[`message-container-${role}`],
        customStyles && customStyles[`message-container-${role}`],
        'uitest-chat-message'
      )}
    >
      <div className={moduleStyles.grid}>
        {isAssistant && (
          <div
            className={classNames(
              moduleStyles.botIconWrapper,
              isTA && moduleStyles.botIconContainerWithOverlay
            )}
          >
            <div className={classNames(moduleStyles.botIconContainer)}>
              <img
                src={aiBotOutlineIcon}
                alt={commonI18n.aiChatBotIconAlt()}
                className={moduleStyles.botIcon}
              />
            </div>
            {isTA && (
              <div className={moduleStyles.botOverlay}>
                <span>{'TA'}</span>
              </div>
            )}
          </div>
        )}
        <div className={moduleStyles[`message-and-header-${role}`]}>
          {header && <div className={moduleStyles.header}>{header}</div>}
          {(text || postText) && (
            <div
              className={classNames(
                moduleStyles[`message-${role}`],
                customStyles && customStyles[`message-${role}`],
                messageStyle === 'danger' && moduleStyles.danger,
                messageStyle === 'warning' && moduleStyles.warning
              )}
              aria-label={
                role === Role.ASSISTANT
                  ? commonI18n.aiChatMessageBot()
                  : commonI18n.aiChatMessageUser()
              }
            >
              {role === Role.ASSISTANT ? (
                <div className={moduleStyles.assistantMessageContent}>
                  <SafeMarkdown
                    markdown={text}
                    rehypeMap={rehypeMap}
                    openExternalLinksInNewTab
                  />
                  {showAiTutorVersionActions && (
                    <AiTutorVersionActions files={aiTutorVersionFiles} />
                  )}
                  {postText}
                </div>
              ) : (
                <p>{text}</p>
              )}
            </div>
          )}
        </div>
        {footer && (
          <div
            className={classNames(isAssistant && moduleStyles.assistantFooter)}
          >
            {footer}
          </div>
        )}
      </div>
    </div>
  );
};

export default ChatMessage;
